import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, InputNumber, Input, Select, Upload, Button, message, Row, Col, Table, Space, Tooltip, Badge } from "antd";
import { UploadOutlined, DeleteOutlined, FilePdfOutlined, PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useFetchInventoryFilterOptionsQuery, useFetchInventoryQuery } from "features/store";

const { Option } = Select;

const AddQuantityModal = ({ open, onCancel, onOk, inventoryId: propInventoryId, loading = false, isUpdateMode = false, materialType = "CLOTH MATERIAL" }) => {
  const [form] = Form.useForm();
  const [documentType, setDocumentType] = useState(null);
  const [billFile, setBillFile] = useState(null);
  const [challanFile, setChallanFile] = useState(null);
  const [billPreview, setBillPreview] = useState(null);
  const [challanPreview, setChallanPreview] = useState(null);
  const [selectedFabricType, setSelectedFabricType] = useState(null);
  const [selectedSubType, setSelectedSubType] = useState(null);
  const [fabricTypeSearch, setFabricTypeSearch] = useState('');
  const [fabricColorSearch, setFabricColorSearch] = useState('');
  const [subTypeSearch, setSubTypeSearch] = useState('');
  const [itemsList, setItemsList] = useState([]); // For multiple items
  const [currentItem, setCurrentItem] = useState({ // For adding new item to list
    fabric_type: null,
    sub_type: null,
    fabric_color: null,
    quantity: null,
    rate: null
  });

  // Fetch all fabric types and colors without pagination
  const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useFetchInventoryFilterOptionsQuery(
    { type: materialType },
    { skip: !open }
  );

  // Fetch all inventory items to get unique cloth types
  const { data: allInventoryData } = useFetchInventoryQuery(
    {
      page: 1,
      page_size: 10000, // Large number to get all without pagination
      type: materialType,
    },
    { skip: !open }
  );

  // Extract unique fabric types (cloth_type) or cap materials based on materialType
  const fabricTypes = useMemo(() => {
    if (!allInventoryData) return [];
    const inventoryList = Array.isArray(allInventoryData) 
      ? allInventoryData 
      : (allInventoryData?.results || []);
    
    const types = new Set();
    inventoryList.forEach((item) => {
      if (materialType === "CAP MATERIAL") {
        const capMaterial = item?.color_cloth?.cap_material;
        if (capMaterial) {
          types.add(capMaterial);
        }
      } else {
        const clothType = item?.color_cloth?.cloth_type;
        if (clothType) {
          types.add(clothType);
        }
      }
    });
    return Array.from(types).sort();
  }, [allInventoryData, materialType]);

  // Extract sub-types for selected cap material (ColorCloth.type)
  const subTypes = useMemo(() => {
    if (!allInventoryData || !selectedFabricType || materialType !== "CAP MATERIAL") return [];
    const inventoryList = Array.isArray(allInventoryData)
      ? allInventoryData
      : (allInventoryData?.results || []);
    const types = new Set();
    inventoryList.forEach((item) => {
      const capMaterial = item?.color_cloth?.cap_material;
      const subType = item?.color_cloth?.type;
      if (capMaterial === selectedFabricType && subType) {
        types.add(subType);
      }
    });
    return Array.from(types).sort();
  }, [allInventoryData, selectedFabricType, materialType]);

  // Extract sub-types for current item in add-list mode
  const currentItemSubTypes = useMemo(() => {
    if (!allInventoryData || !currentItem.fabric_type || materialType !== "CAP MATERIAL") return [];
    const inventoryList = Array.isArray(allInventoryData)
      ? allInventoryData
      : (allInventoryData?.results || []);
    const types = new Set();
    inventoryList.forEach((item) => {
      const capMaterial = item?.color_cloth?.cap_material;
      const subType = item?.color_cloth?.type;
      if (capMaterial === currentItem.fabric_type && subType) {
        types.add(subType);
      }
    });
    return Array.from(types).sort();
  }, [allInventoryData, currentItem.fabric_type, materialType]);

  // Extract colors based on selected fabric type (for single item mode)
  const fabricColors = useMemo(() => {
    if (!allInventoryData || !selectedFabricType) return [];
    const inventoryList = Array.isArray(allInventoryData) 
      ? allInventoryData 
      : (allInventoryData?.results || []);
    
    const colors = new Set();
    inventoryList.forEach((item) => {
      if (materialType === "CAP MATERIAL") {
        const capMaterial = item?.color_cloth?.cap_material;
        const subType = item?.color_cloth?.type;
        const color = item?.color_cloth?.color;
        // If a sub-type is selected, filter by it; otherwise include all
        const subTypeOk = selectedSubType ? subType === selectedSubType : true;
        if (capMaterial === selectedFabricType && subTypeOk && color) {
          colors.add(color);
        }
      } else {
        const clothType = item?.color_cloth?.cloth_type;
        const color = item?.color_cloth?.color;
        if (clothType === selectedFabricType && color) {
          colors.add(color);
        }
      }
    });
    return Array.from(colors).sort();
  }, [allInventoryData, selectedFabricType, selectedSubType, materialType]);

  // Extract colors based on current item fabric type (for multiple items mode)
  const currentItemFabricColors = useMemo(() => {
    if (!allInventoryData || !currentItem.fabric_type) return [];
    const inventoryList = Array.isArray(allInventoryData) 
      ? allInventoryData 
      : (allInventoryData?.results || []);
    
    const colors = new Set();
    inventoryList.forEach((item) => {
      if (materialType === "CAP MATERIAL") {
        const capMaterial = item?.color_cloth?.cap_material;
        const subType = item?.color_cloth?.type;
        const color = item?.color_cloth?.color;
        const subTypeOk = currentItem.sub_type ? subType === currentItem.sub_type : true;
        if (capMaterial === currentItem.fabric_type && subTypeOk && color) {
          colors.add(color);
        }
      } else {
        const clothType = item?.color_cloth?.cloth_type;
        const color = item?.color_cloth?.color;
        if (clothType === currentItem.fabric_type && color) {
          colors.add(color);
        }
      }
    });
    return Array.from(colors).sort();
  }, [allInventoryData, currentItem.fabric_type, currentItem.sub_type, materialType]);

  // Filter fabric types based on search
  const filteredFabricTypes = useMemo(() => {
    if (!fabricTypeSearch) return fabricTypes;
    return fabricTypes.filter(type => 
      type.toLowerCase().includes(fabricTypeSearch.toLowerCase())
    );
  }, [fabricTypes, fabricTypeSearch]);

  // Filter fabric colors based on search (for single item mode)
  const filteredFabricColors = useMemo(() => {
    if (!fabricColorSearch) return fabricColors;
    return fabricColors.filter(color => 
      color.toLowerCase().includes(fabricColorSearch.toLowerCase())
    );
  }, [fabricColors, fabricColorSearch]);

  // Filter sub-types for single-item (uses selected cap material) and multi-items (uses currentItem.fabric_type)
  const filteredSubTypesSingle = useMemo(() => {
    const list = subTypes;
    if (!subTypeSearch) return list;
    return list.filter(st => st.toLowerCase().includes(subTypeSearch.toLowerCase()));
  }, [subTypes, subTypeSearch]);

  const filteredSubTypesList = useMemo(() => {
    const list = currentItemSubTypes;
    if (!subTypeSearch) return list;
    return list.filter(st => st.toLowerCase().includes(subTypeSearch.toLowerCase()));
  }, [currentItemSubTypes, subTypeSearch]);

  // Filter fabric colors for current item (for multiple items mode)
  const filteredCurrentItemFabricColors = useMemo(() => {
    if (!fabricColorSearch) return currentItemFabricColors;
    return currentItemFabricColors.filter(color => 
      color.toLowerCase().includes(fabricColorSearch.toLowerCase())
    );
  }, [currentItemFabricColors, fabricColorSearch]);

  // Watch for fabric color changes to update current quantity
  const fabricColor = Form.useWatch('fabric_color', form);

  // Get current quantity for selected fabric type and color
  const currentQuantity = useMemo(() => {
    if (!allInventoryData || !selectedFabricType || !fabricColor) return null;
    
    const inventoryList = Array.isArray(allInventoryData) 
      ? allInventoryData 
      : (allInventoryData?.results || []);
    
    const inventoryItem = inventoryList.find((item) => {
      if (materialType === "CAP MATERIAL") {
        const capMaterial = item?.color_cloth?.cap_material;
        const color = item?.color_cloth?.color;
        return capMaterial === selectedFabricType && color === fabricColor;
      } else {
        const clothType = item?.color_cloth?.cloth_type;
        const color = item?.color_cloth?.color;
        return clothType === selectedFabricType && color === fabricColor;
      }
    });
    
    // Return quantity even if it's 0, or null if item doesn't exist
    if (inventoryItem) {
      return inventoryItem.quantity ?? 0;
    }
    return null;
  }, [allInventoryData, selectedFabricType, fabricColor, materialType]);

  // Get current quantity for current item (multiple items mode)
  const currentItemQuantity = useMemo(() => {
    if (!allInventoryData || !currentItem.fabric_type || !currentItem.fabric_color) return null;
    
    const inventoryList = Array.isArray(allInventoryData) 
      ? allInventoryData 
      : (allInventoryData?.results || []);
    
    const inventoryItem = inventoryList.find((item) => {
      if (materialType === "CAP MATERIAL") {
        const capMaterial = item?.color_cloth?.cap_material;
        const color = item?.color_cloth?.color;
        return capMaterial === currentItem.fabric_type && color === currentItem.fabric_color;
      } else {
        const clothType = item?.color_cloth?.cloth_type;
        const color = item?.color_cloth?.color;
        return clothType === currentItem.fabric_type && color === currentItem.fabric_color;
      }
    });
    
    // Return quantity even if it's 0, or null if item doesn't exist
    if (inventoryItem) {
      return inventoryItem.quantity ?? 0;
    }
    return null;
  }, [allInventoryData, currentItem.fabric_type, currentItem.fabric_color, materialType]);

  // Reset color when fabric type changes
  useEffect(() => {
    if (selectedFabricType) {
      form.setFieldsValue({ sub_type: undefined, fabric_color: undefined, quantity: undefined });
      setSelectedSubType(null);
    }
  }, [selectedFabricType, form]);

  // Pre-fill quantity when fabric type and color are selected in update mode
  useEffect(() => {
    if (isUpdateMode && selectedFabricType && fabricColor) {
      if (currentQuantity !== null && currentQuantity !== undefined) {
        form.setFieldsValue({ quantity: currentQuantity });
      } else {
        // If no inventory found, clear quantity field
        form.setFieldsValue({ quantity: undefined });
      }
    }
  }, [isUpdateMode, selectedFabricType, fabricColor, currentQuantity, form]);

  const handleDocumentTypeChange = (value) => {
    setDocumentType(value);
    // Clear files when switching document type
    if (value !== 'bill') {
      setBillFile(null);
      form.setFieldsValue({ bill_proof: undefined });
    }
    if (value !== 'challan') {
      setChallanFile(null);
      form.setFieldsValue({ challan_proof: undefined });
    }
  };

  const handleBillFileChange = (file) => {
    setBillFile(file);
    // Create preview for images
    if (file && file.type && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBillPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setBillPreview(null);
    }
  };

  const handleChallanFileChange = (file) => {
    setChallanFile(file);
    // Create preview for images
    if (file && file.type && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setChallanPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setChallanPreview(null);
    }
  };

  const handleRemoveBill = () => {
    setBillFile(null);
    setBillPreview(null);
    form.setFieldsValue({ bill_proof: undefined });
  };

  const handleRemoveChallan = () => {
    setChallanFile(null);
    setChallanPreview(null);
    form.setFieldsValue({ challan_proof: undefined });
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Validate file uploads based on document type (only for add mode with items)
      if (!isUpdateMode && documentType) {
      if (documentType === 'bill' && !billFile) {
        message.error('Please upload bill proof');
        return;
      }
      if (documentType === 'challan' && !challanFile) {
        message.error('Please upload challan proof');
        return;
      }
      }

      // Check if we have multiple items or single item
      // Include currentItem if it's filled but not yet added to the list
      const hasCurrentItem = currentItem.fabric_type && currentItem.fabric_color && currentItem.quantity;
      const hasMultipleItems = itemsList.length > 0 || hasCurrentItem;
      
      if (hasMultipleItems && !isUpdateMode) {
        // Multiple items mode - collect all items including currentItem if filled
        const itemsToSend = [...itemsList];
        
        // Add currentItem if it's filled but not in the list
        if (hasCurrentItem) {
          // Check if currentItem is already in itemsList (avoid duplicates)
          const isDuplicate = itemsList.some(item => 
            item.fabric_type === currentItem.fabric_type && 
            item.fabric_color === currentItem.fabric_color
          );
          
          if (!isDuplicate) {
            itemsToSend.push({
              fabric_type: currentItem.fabric_type,
              fabric_color: currentItem.fabric_color,
              quantity: parseFloat(currentItem.quantity),
              rate: currentItem.rate ? parseFloat(currentItem.rate) : 0
            });
          }
        }
        
        if (itemsToSend.length === 0) {
          message.error('Please add at least one fabric item');
          return;
        }

        // Map items to send format
        const itemsToSendFormatted = itemsToSend.map(item => {
          if (materialType === "CAP MATERIAL") {
            return {
              cap_material: item.fabric_type, // fabric_type contains cap_material for CAP MATERIAL
              sub_type: item.sub_type || null,
              color: item.fabric_color, // fabric_color contains color for CAP MATERIAL
              quantity: item.quantity,
              rate: item.rate || 0
            };
          } else {
            return {
              fabric_type: item.fabric_type,
              fabric_color: item.fabric_color,
              quantity: item.quantity,
              rate: item.rate || 0
            };
          }
        });
        
        console.log('Items list state:', itemsList);
        console.log('Current item:', currentItem);
        console.log('Items to send:', itemsToSendFormatted);
        console.log('Total items to send:', itemsToSendFormatted.length);
        
        const formData = {
          document_type: documentType,
          items: itemsToSendFormatted
        };

        if (documentType === 'bill' && values.bill_number) {
          formData.bill_number = values.bill_number;
          if (billFile) {
            formData.bill_proof = billFile;
          }
        } else if (documentType === 'challan' && values.challan_number) {
          formData.challan_number = values.challan_number;
          if (challanFile) {
            formData.challan_proof = challanFile;
          }
        }

        onOk(formData);
        
        // Reset form
        form.resetFields();
        setDocumentType(null);
        setBillFile(null);
        setChallanFile(null);
        setBillPreview(null);
        setChallanPreview(null);
        setItemsList([]);
        setCurrentItem({ fabric_type: null, fabric_color: null, quantity: null, rate: null });
        setSelectedFabricType(null);
        setFabricTypeSearch('');
        setFabricColorSearch('');
        return;
      }

      // Single item mode (original flow) - Validate fabric type, (sub-type if available), and color
      if (!values.fabric_type || !values.fabric_color) {
        message.error(materialType === "CAP MATERIAL" ? 'Please select cap material and color' : 'Please select fabric type and color');
        return;
      }

      // When CAP MATERIAL and sub-types exist, require sub_type selection
      if (materialType === "CAP MATERIAL") {
        const hasSubTypes = subTypes && subTypes.length > 0;
        if (hasSubTypes && !values.sub_type) {
          message.error('Please select type for the selected cap material');
          return;
        }
      }

      // Find existing inventory item
      const inventoryList = Array.isArray(allInventoryData) 
        ? allInventoryData 
        : (allInventoryData?.results || []);
      
      const existingInventory = inventoryList.find((item) => {
        if (materialType === "CAP MATERIAL") {
          const capMaterial = item?.color_cloth?.cap_material;
          const subType = item?.color_cloth?.type;
          const color = item?.color_cloth?.color;
          const subTypeOk = values.sub_type ? subType === values.sub_type : true;
          return capMaterial === values.fabric_type && subTypeOk && color === values.fabric_color;
        } else {
          const clothType = item?.color_cloth?.cloth_type;
          const color = item?.color_cloth?.color;
          return clothType === values.fabric_type && color === values.fabric_color;
        }
      });

      const formData = {
        quantity: values.quantity,
        rate: values.rate || 0,
      };

      // If update mode, require existing inventory
      if (isUpdateMode) {
        if (!existingInventory) {
          message.error('No inventory found for selected fabric type and color. Please use Add button to create new inventory.');
          return;
        }
        formData.inventoryId = existingInventory.id;
        formData.id = existingInventory.id;
        // For update, we can also include color_cloth ID if needed
        if (existingInventory.color_cloth?.id) {
          formData.color_cloth = existingInventory.color_cloth.id;
        }
      } else {
        // Add mode: If inventory exists, update it; otherwise create new
        if (existingInventory) {
          formData.inventoryId = existingInventory.id;
          formData.id = existingInventory.id;
          // For update, we can also include color_cloth ID if needed
          if (existingInventory.color_cloth?.id) {
            formData.color_cloth = existingInventory.color_cloth.id;
          }
        } else {
          // For new inventory, send appropriate fields based on materialType
          if (materialType === "CAP MATERIAL") {
            formData.cap_material = values.fabric_type; // fabric_type contains cap_material
            formData.sub_type = values.sub_type || null;
            formData.color = values.fabric_color; // fabric_color contains color
          } else {
            formData.fabric_type = values.fabric_type;
            formData.fabric_color = values.fabric_color;
          }
        }
      }

      // Add document type specific fields (only for add mode)
      if (!isUpdateMode) {
        if (documentType === 'bill' && values.bill_number) {
          formData.bill_number = values.bill_number;
          if (billFile) {
            formData.bill_proof = billFile;
          }
        } else if (documentType === 'challan' && values.challan_number) {
          formData.challan_number = values.challan_number;
          if (challanFile) {
            formData.challan_proof = challanFile;
          }
        }
        // Add rate only in add mode
        if (values.rate) {
          formData.rate = values.rate;
        }
      } else {
        // In update mode, use update_quantity to set absolute value
        formData.update_quantity = values.quantity;
      }

      onOk(formData);
      
      // Reset form
      form.resetFields();
      setDocumentType(null);
      setBillFile(null);
      setChallanFile(null);
      setBillPreview(null);
      setChallanPreview(null);
      setSelectedFabricType(null);
      setFabricTypeSearch('');
      setFabricColorSearch('');
      setItemsList([]);
      setCurrentItem({ fabric_type: null, fabric_color: null, quantity: null, rate: null });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setDocumentType(null);
    setBillFile(null);
    setChallanFile(null);
    setBillPreview(null);
    setChallanPreview(null);
    setSelectedFabricType(null);
    setFabricTypeSearch('');
    setFabricColorSearch('');
    setItemsList([]);
    setCurrentItem({ fabric_type: null, fabric_color: null, quantity: null, rate: null });
    onCancel();
  };

  // Handle adding item to the list
  const handleAddItemToList = () => {
    const needSubType = materialType === "CAP MATERIAL" && currentItemSubTypes && currentItemSubTypes.length > 0;
    if (!currentItem.fabric_type || (needSubType && !currentItem.sub_type) || !currentItem.fabric_color || !currentItem.quantity) {
      message.error(materialType === "CAP MATERIAL"
        ? (needSubType ? 'Please fill in Cap Material, Type, Color, and Quantity' : 'Please fill in Cap Material, Color, and Quantity')
        : 'Please fill in Fabric Type, Fabric Color, and Quantity');
      return;
    }
    
    const newItem = {
      id: Date.now(), // Temporary ID for React keys
      fabric_type: currentItem.fabric_type,
      sub_type: currentItem.sub_type || null,
      fabric_color: currentItem.fabric_color,
      quantity: parseFloat(currentItem.quantity),
      rate: currentItem.rate ? parseFloat(currentItem.rate) : null
    };
    
    setItemsList([...itemsList, newItem]);
    // Clear current item for next entry
    setCurrentItem({ fabric_type: null, sub_type: null, fabric_color: null, quantity: null, rate: null });
  };

  // Handle updating an item in the list
  const handleUpdateItemInList = (itemId, field, value) => {
    setItemsList(itemsList.map(item => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Get current quantity for an item in the list
  const getItemCurrentQuantity = (fabricType, fabricColor) => {
    if (!allInventoryData || !fabricType || !fabricColor) return null;
    
    const inventoryList = Array.isArray(allInventoryData) 
      ? allInventoryData 
      : (allInventoryData?.results || []);
    
    const inventoryItem = inventoryList.find((item) => {
      if (materialType === "CAP MATERIAL") {
        const capMaterial = item?.color_cloth?.cap_material;
        const subType = item?.color_cloth?.type;
        const color = item?.color_cloth?.color;
        const subTypeOk = currentItem.sub_type ? subType === currentItem.sub_type : true;
        return capMaterial === fabricType && subTypeOk && color === fabricColor;
      } else {
        const clothType = item?.color_cloth?.cloth_type;
        const color = item?.color_cloth?.color;
        return clothType === fabricType && color === fabricColor;
      }
    });
    
    if (inventoryItem) {
      return inventoryItem.quantity ?? 0;
    }
    return null;
  };

  // Handle removing item from the list
  const handleRemoveItem = (itemId) => {
    setItemsList(itemsList.filter(item => item.id !== itemId));
  };

  // Custom File Upload Component
  const FileUploadBox = ({ file, preview, onFileSelect, onRemove, accept, id }) => {
    const inputId = `file-upload-input-${id || Math.random()}`;
    
    const handleFileSelect = (e) => {
      const selectedFile = e.target.files[0];
      if (selectedFile) {
        onFileSelect(selectedFile);
      }
    };

    const isImageFile = (file) => {
      return file && file.type && file.type.startsWith('image/');
    };

    const isPdfFile = (file) => {
      return file && (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf'));
    };

    return (
      <UploadBoxContainer>
        {file ? (
          <FilePreviewBox>
            <DeleteButton onClick={onRemove} icon={<DeleteOutlined />} />
            {isImageFile(file) && preview ? (
              <ImagePreview src={preview} alt="Preview" />
            ) : isPdfFile(file) ? (
              <PdfPreview>
                <FilePdfOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                <FileName>{file.name}</FileName>
              </PdfPreview>
            ) : (
              <FilePreview>
                <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                <FileName>{file.name}</FileName>
              </FilePreview>
            )}
          </FilePreviewBox>
        ) : (
          <UploadPlaceholder>
            <input
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id={inputId}
            />
            <label htmlFor={inputId}>
              <UploadIcon>
                <UploadOutlined style={{ fontSize: '32px', color: '#8c8c8c' }} />
              </UploadIcon>
              <UploadText>Click to upload</UploadText>
            </label>
          </UploadPlaceholder>
        )}
      </UploadBoxContainer>
    );
  };

  return (
    <>
      <style>
        {`
          .add-quantity-modal .ant-modal-content {
            border-radius: 16px !important;
            overflow: hidden;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
          }
          .add-quantity-modal .ant-modal-header {
            border-radius: 16px 16px 0 0 !important;
            flex-shrink: 0;
            border-bottom: 1px solid #f0f0f0;
            padding: 20px 24px;
          }
          .add-quantity-modal .ant-modal-body {
            border-radius: 0 !important;
            overflow-y: auto;
            flex: 1;
            max-height: calc(90vh - 110px);
            padding: 24px;
          }
          .add-quantity-modal .ant-modal-footer {
            border-radius: 0 0 16px 16px !important;
            border-top: 1px solid #f0f0f0;
            padding: 16px 24px;
            flex-shrink: 0;
          }
        `}
      </style>
      <Modal
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        cancelText="Cancel"
        width={1200}
        destroyOnClose
        centered
        rootClassName="add-quantity-modal"
        confirmLoading={loading}
        maskClosable={!loading}
        closable={!loading}
        okButtonProps={{ disabled: loading }}
        cancelButtonProps={{ disabled: loading }}
        title={isUpdateMode ? "Update Quantity" : "Add Quantity"}
        okText={isUpdateMode ? "Update" : "Add"}
        styles={{
          content: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxHeight: '90vh',
          },
          header: {
            borderRadius: '16px 16px 0 0',
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px',
          },
          body: {
            borderRadius: 0,
            maxHeight: 'calc(90vh - 110px)',
            overflowY: 'auto',
            padding: '24px',
          },
          footer: {
            borderRadius: '0 0 16px 16px',
            borderTop: '1px solid #f0f0f0',
            padding: '16px 24px',
          },
        }}
      >
      <ModalContent>
        <Form
          form={form}
          layout="vertical"
          disabled={loading}
          initialValues={{
            quantity: undefined,
            rate: 0,
          }}
        >
          {!isUpdateMode && (
            <>
              <Row gutter={16}>
                <Col span={12}>
              <Form.Item
                name="document_type"
                label="Select Document"
              >
                <Select
                  placeholder="Select document type"
                  allowClear
                  onChange={handleDocumentTypeChange}
                  value={documentType}
                >
                  <Option value="bill">Bill</Option>
                  <Option value="challan">Challan</Option>
                </Select>
              </Form.Item>
                </Col>

              {documentType === 'bill' && (
                  <Col span={12}>
              <Form.Item
                name="bill_number"
                label="Bill Number"
                rules={[{ required: true, message: 'Please enter bill number' }]}
              >
                <Input placeholder="Enter bill number" />
              </Form.Item>
                  </Col>
                )}

                {documentType === 'challan' && (
                  <Col span={12}>
                    <Form.Item
                      name="challan_number"
                      label="Challan Number"
                      rules={[{ required: true, message: 'Please enter challan number' }]}
                    >
                      <Input placeholder="Enter challan number" />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {documentType === 'bill' && (
              <Form.Item
                name="bill_proof"
                label="Upload Bill Proof"
              >
                <FileUploadBox
                  file={billFile}
                  preview={billPreview}
                  onFileSelect={handleBillFileChange}
                  onRemove={handleRemoveBill}
                  accept="image/*,.pdf"
                  id="bill-proof"
                />
              </Form.Item>
          )}

              {documentType === 'challan' && (
                  <Form.Item
                    name="challan_proof"
                    label="Upload Challan Proof"
                  >
                    <FileUploadBox
                      file={challanFile}
                      preview={challanPreview}
                      onFileSelect={handleChallanFileChange}
                      onRemove={handleRemoveChallan}
                      accept="image/*,.pdf"
                      id="challan-proof"
                    />
                  </Form.Item>
              )}
                </>
              )}

          {/* Multiple Items Section - Only in Add Mode */}
          {!isUpdateMode && documentType && (
            <>
              <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <div style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
                  Add Fabric Items
                </div>
                
                {/* Display added items as input field rows */}
                {itemsList.map((item, index) => {
                  const itemCurrentQty = getItemCurrentQuantity(item.fabric_type, item.fabric_color);
                  return (
                    <Row gutter={16} align="bottom" key={item.id} style={{ marginBottom: '16px' }}>
                      <Col span={5}>
                        <Form.Item label={index === 0 ? (materialType === "CAP MATERIAL" ? "Cap Material" : "Fabric Type") : ""}>
                          <Select
                            value={item.fabric_type}
                            onChange={(value) => handleUpdateItemInList(item.id, 'fabric_type', value)}
                            showSearch
                            allowClear
                            filterOption={false}
                            onSearch={setFabricTypeSearch}
                            loading={isLoadingFilterOptions}
                            dropdownRender={(menu) => (
                              <>
                                <div style={{ padding: '8px' }}>
                                  <Input
                                    placeholder="Search fabric type"
                                    value={fabricTypeSearch}
                                    onChange={(e) => setFabricTypeSearch(e.target.value)}
                                    allowClear
                                  />
                                </div>
                                {menu}
            </>
          )}
                          >
                            {filteredFabricTypes.map((type) => (
                              <Option key={type} value={type}>
                                {type}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      {materialType === "CAP MATERIAL" && currentItemSubTypes.length > 0 && (
                        <Col span={5}>
                          <Form.Item label={index === 0 ? "Type" : ""}>
                            <Select
                              value={item.sub_type}
                              onChange={(value) => handleUpdateItemInList(item.id, 'sub_type', value)}
                              showSearch
                              allowClear
                              disabled={!item.fabric_type}
                              filterOption={false}
                              onSearch={setSubTypeSearch}
                              loading={isLoadingFilterOptions}
                              dropdownRender={(menu) => (
                                <>
                                  <div style={{ padding: '8px' }}>
                                    <Input
                                      placeholder="Search type"
                                      value={subTypeSearch}
                                      onChange={(e) => setSubTypeSearch(e.target.value)}
                                      allowClear
                                    />
                                  </div>
                                  {menu}
                                </>
                              )}
                            >
                              {filteredSubTypesList.map((st) => (
                                <Option key={st} value={st}>
                                  {st}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      )}

                      <Col span={5}>
                        <Form.Item label={index === 0 ? "Fabric Color" : ""}>
                          <Select
                            value={item.fabric_color}
                            onChange={(value) => handleUpdateItemInList(item.id, 'fabric_color', value)}
                            showSearch
                            allowClear
                            disabled={!item.fabric_type || (materialType === "CAP MATERIAL" && currentItemSubTypes.length > 0 && !item.sub_type)}
                            filterOption={false}
                            onSearch={setFabricColorSearch}
                            loading={isLoadingFilterOptions}
                            dropdownRender={(menu) => (
                              <>
                                <div style={{ padding: '8px' }}>
                                  <Input
                                    placeholder="Search fabric color"
                                    value={fabricColorSearch}
                                    onChange={(e) => setFabricColorSearch(e.target.value)}
                                    allowClear
                                  />
                                </div>
                                {menu}
                              </>
                            )}
                          >
                            {(() => {
                              if (!item.fabric_type || !allInventoryData) return [];
                              const inventoryList = Array.isArray(allInventoryData) 
                                ? allInventoryData 
                                : (allInventoryData?.results || []);
                              const colors = new Set();
                              inventoryList.forEach((invItem) => {
                                if (materialType === "CAP MATERIAL") {
                                  const capMaterial = invItem?.color_cloth?.cap_material;
                                  const subType = invItem?.color_cloth?.type;
                                  const color = invItem?.color_cloth?.color;
                                  const subTypeOk = item.sub_type ? subType === item.sub_type : true;
                                  if (capMaterial === item.fabric_type && subTypeOk && color) {
                                    colors.add(color);
                                  }
                                } else {
                                  const clothType = invItem?.color_cloth?.cloth_type;
                                  const color = invItem?.color_cloth?.color;
                                  if (clothType === item.fabric_type && color) {
                                    colors.add(color);
                                  }
                                }
                              });
                              return Array.from(colors).sort().map((color) => (
                                <Option key={color} value={color}>
                                  {color}
                                </Option>
                              ));
                            })()}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={5}>
                        <Form.Item 
                          label={
                            <span>
                              {index === 0 ? "Quantity" : ""}
                              {itemCurrentQty !== null && (
                                <span style={{
                                  display: 'inline-block',
                                  backgroundColor: '#ff4d4f',
                                  color: '#fff',
                                  borderRadius: '10px',
                                  padding: '2px 8px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  marginLeft: '8px',
                                  lineHeight: '1.5'
                                }}>
                                  {itemCurrentQty}
                                </span>
                              )}
                            </span>
                          }
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Enter quantity"
                            min={0}
                            step={0.01}
                            value={item.quantity}
                            onChange={(value) => handleUpdateItemInList(item.id, 'quantity', value)}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={5}>
                        <Form.Item label={index === 0 ? "Rate (Optional)" : ""}>
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Enter rate per unit"
                            min={0}
                            step={0.01}
                            value={item.rate}
                            onChange={(value) => handleUpdateItemInList(item.id, 'rate', value)}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={4} style={{ paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'flex-end', paddingBottom: '25px', justifyContent: 'center' }}>
                    <Tooltip title="Remove">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />}
                        onClick={() => {
                          handleRemoveItem(item.id);
                        }}
                        style={{
                          padding: 0,
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                    </Tooltip>
                  </Col>
                    </Row>
                  );
                })}

                {/* Add Item Form */}
                <Row gutter={16} align="bottom">
                  <Col span={5}>
                    <Form.Item
                      label={materialType === "CAP MATERIAL" ? "Cap Material" : "Fabric Type"}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Select
                        placeholder={materialType === "CAP MATERIAL" ? "Select cap material" : "Select fabric type"}
                        showSearch
                        allowClear
                        filterOption={false}
                        onSearch={setFabricTypeSearch}
                        value={currentItem.fabric_type}
                        onChange={(value) => {
                          setCurrentItem({ ...currentItem, fabric_type: value, sub_type: null, fabric_color: null });
                        }}
                        loading={isLoadingFilterOptions}
                        dropdownRender={(menu) => (
                          <>
                            <div style={{ padding: '8px' }}>
                              <Input
                                placeholder="Search fabric type"
                                value={fabricTypeSearch}
                                onChange={(e) => setFabricTypeSearch(e.target.value)}
                                allowClear
                              />
                            </div>
                            {menu}
                          </>
                        )}
                      >
                        {filteredFabricTypes.map((type) => (
                          <Option key={type} value={type}>
                            {type}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {materialType === "CAP MATERIAL" && currentItemSubTypes.length > 0 && (
                    <Col span={5}>
                      <Form.Item label="Type">
                        <Select
                          placeholder={currentItem.fabric_type ? "Select type" : "Select cap material first"}
                          showSearch
                          allowClear
                          disabled={!currentItem.fabric_type}
                          filterOption={false}
                          onSearch={setSubTypeSearch}
                          value={currentItem.sub_type}
                          onChange={(value) => {
                            setCurrentItem({ ...currentItem, sub_type: value, fabric_color: null });
                          }}
                          loading={isLoadingFilterOptions}
                          dropdownRender={(menu) => (
                            <>
                              <div style={{ padding: '8px' }}>
                                <Input
                                  placeholder="Search type"
                                  value={subTypeSearch}
                                  onChange={(e) => setSubTypeSearch(e.target.value)}
                                  allowClear
                                />
                              </div>
                              {menu}
                            </>
                          )}
                        >
                          {filteredSubTypesList.map((st) => (
                            <Option key={st} value={st}>
                              {st}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}

                  <Col span={5}>
                    <Form.Item
                      label="Fabric Color"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Select
                        placeholder={currentItem.fabric_type ? (materialType === "CAP MATERIAL" && currentItemSubTypes.length > 0 && !currentItem.sub_type ? "Please select type first" : "Select fabric color") : (materialType === "CAP MATERIAL" ? "Please select cap material first" : "Select fabric type first")}
                        showSearch
                        allowClear
                        disabled={!currentItem.fabric_type || (materialType === "CAP MATERIAL" && currentItemSubTypes.length > 0 && !currentItem.sub_type)}
                        filterOption={false}
                        onSearch={setFabricColorSearch}
                        value={currentItem.fabric_color}
                        onChange={(value) => {
                          setCurrentItem({ ...currentItem, fabric_color: value });
                        }}
                        loading={isLoadingFilterOptions}
                        dropdownRender={(menu) => (
                          <>
                            <div style={{ padding: '8px' }}>
                              <Input
                                placeholder="Search fabric color"
                                value={fabricColorSearch}
                                onChange={(e) => setFabricColorSearch(e.target.value)}
                                allowClear
                              />
                            </div>
                            {menu}
                          </>
                        )}
                      >
                        {filteredCurrentItemFabricColors.map((color) => (
                          <Option key={color} value={color}>
                            {color}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={5}>
                    <Form.Item
                      label={
                        <span>
                          Quantity
                          {currentItemQuantity !== null && (
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: '#ff4d4f',
                              color: '#fff',
                              borderRadius: '10px',
                              padding: '2px 8px',
                              fontSize: '12px',
                              fontWeight: '500',
                              marginLeft: '8px',
                              lineHeight: '1.5'
                            }}>
                              {currentItemQuantity}
                            </span>
                          )}
                        </span>
                      }
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Enter quantity"
                        min={0}
                        step={0.01}
                        value={currentItem.quantity}
                        onChange={(value) => {
                          setCurrentItem({ ...currentItem, quantity: value });
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={5}>
                    <Form.Item
                      label="Rate (Optional)"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Enter rate per unit"
                        min={0}
                        step={0.01}
                        value={currentItem.rate}
                        onChange={(value) => {
                          setCurrentItem({ ...currentItem, rate: value });
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={4} style={{ paddingLeft: '8px', paddingRight: '8px', display: 'flex', alignItems: 'flex-end', paddingBottom: '25px', justifyContent: 'center' }}>
                    <Tooltip title="Remove">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />}
                        onClick={() => {
                          setCurrentItem({ fabric_type: null, fabric_color: null, quantity: null, rate: null });
                        }}
                        style={{
                          padding: 0,
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                    </Tooltip>
                  </Col>
                </Row>

                {/* Add Button Row - Left aligned below Fabric Type */}
                <Row gutter={16}>
                  <Col span={6}>
                    <Tooltip title="add more">
                      <Button
                        type="primary"
                        shape="square"
                        icon={<PlusOutlined style={{ color: '#fff', fontSize: '16px' }} />}
                        onClick={handleAddItemToList}
                        style={{
                          width: '40px',
                          height: '40px',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#1890ff',
                          borderColor: '#1890ff'
                        }}
                      />
                    </Tooltip>
                  </Col>
                </Row>
              </div>
            </>
          )}

          {/* Single Item Section - For Update Mode or when no document type selected */}
          {(!documentType || isUpdateMode) && (
            <Row gutter={16}>
              <Col span={isUpdateMode ? 8 : 6}>
          <Form.Item
            name="fabric_type"
            label={materialType === "CAP MATERIAL" ? "Cap Material" : "Fabric Type"}
            rules={[{ required: true, message: materialType === "CAP MATERIAL" ? 'Please select cap material' : 'Please select fabric type' }]}
          >
            <Select
              placeholder={materialType === "CAP MATERIAL" ? "Select cap material" : "Select fabric type"}
              showSearch
              allowClear
              filterOption={false}
              onSearch={setFabricTypeSearch}
              onChange={(value) => {
                setSelectedFabricType(value);
                form.setFieldsValue({ sub_type: undefined, fabric_color: undefined });
                setSelectedSubType(null);
              }}
              loading={isLoadingFilterOptions}
              dropdownRender={(menu) => (
                <>
                  <div style={{ padding: '8px' }}>
                    <Input
                      placeholder="Search fabric type"
                      value={fabricTypeSearch}
                      onChange={(e) => setFabricTypeSearch(e.target.value)}
                      allowClear
                    />
                  </div>
                  {menu}
                </>
              )}
            >
              {filteredFabricTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
              </Col>

              {materialType === "CAP MATERIAL" && subTypes.length > 0 && (
                <Col span={isUpdateMode ? 8 : 6}>
          <Form.Item
            name="sub_type"
            label="Type"
          >
            <Select
              placeholder={selectedFabricType ? "Select type" : "Please select cap material first"}
              showSearch
              allowClear
              disabled={!selectedFabricType}
              filterOption={false}
              onSearch={setSubTypeSearch}
              onChange={(value) => {
                setSelectedSubType(value);
                form.setFieldsValue({ fabric_color: undefined });
              }}
              loading={isLoadingFilterOptions}
              dropdownRender={(menu) => (
                <>
                  <div style={{ padding: '8px' }}>
                    <Input
                      placeholder="Search type"
                      value={subTypeSearch}
                      onChange={(e) => setSubTypeSearch(e.target.value)}
                      allowClear
                    />
                  </div>
                  {menu}
                </>
              )}
            >
              {filteredSubTypesSingle.map((st) => (
                <Option key={st} value={st}>
                  {st}
                </Option>
              ))}
            </Select>
          </Form.Item>
                </Col>
              )}

              <Col span={isUpdateMode ? 8 : 6}>
          <Form.Item
            name="fabric_color"
            label="Fabric Color"
            rules={[{ required: true, message: 'Please select fabric color' }]}
          >
            <Select
              placeholder={selectedFabricType ? (materialType === "CAP MATERIAL" && subTypes.length > 0 && !selectedSubType ? "Please select type first" : "Select fabric color") : (materialType === "CAP MATERIAL" ? "Please select cap material first" : "Please select fabric type first")}
              showSearch
              allowClear
              disabled={!selectedFabricType || (materialType === "CAP MATERIAL" && subTypes.length > 0 && !selectedSubType)}
              filterOption={false}
              onSearch={setFabricColorSearch}
              loading={isLoadingFilterOptions}
              dropdownRender={(menu) => (
                <>
                  <div style={{ padding: '8px' }}>
                    <Input
                      placeholder="Search fabric color"
                      value={fabricColorSearch}
                      onChange={(e) => setFabricColorSearch(e.target.value)}
                      allowClear
                    />
                  </div>
                  {menu}
                </>
              )}
            >
              {filteredFabricColors.map((color) => (
                <Option key={color} value={color}>
                  {color}
                </Option>
              ))}
            </Select>
          </Form.Item>
              </Col>

              <Col span={isUpdateMode ? 8 : 6}>
          <Form.Item
            name="quantity"
                  label={
                    <span>
                      Quantity
                      {currentQuantity !== null && (
                        <span style={{
                          display: 'inline-block',
                          backgroundColor: '#ff4d4f',
                          color: '#fff',
                          borderRadius: '10px',
                          padding: '2px 8px',
                          fontSize: '12px',
                          fontWeight: '500',
                          marginLeft: '8px',
                          lineHeight: '1.5'
                        }}>
                          {currentQuantity}
                        </span>
                      )}
                    </span>
                  }
            rules={[
              { required: true, message: 'Please enter quantity' }
            ]}
          >
              <InputNumber
                    style={{ width: '100%' }}
                placeholder={isUpdateMode ? "Enter new quantity" : "Enter quantity"}
                min={0}
                step={0.01}
              />
          </Form.Item>
              </Col>

          {!isUpdateMode && (
                <Col span={6}>
            <Form.Item
              name="rate"
              label="Rate (Optional)"
              rules={[{ type: 'number', min: 0 }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter rate per unit"
                min={0}
                step={0.01}
              />
            </Form.Item>
                </Col>
              )}
            </Row>
          )}
        </Form>
      </ModalContent>
    </Modal>
    </>
  );
};

export default AddQuantityModal;

const ModalContent = styled.div`
  padding: 8px 0;
  
  p {
    margin: 0;
    color: #595959;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const UploadBoxContainer = styled.div`
  width: 100%;
`;

const UploadPlaceholder = styled.div`
  width: 200px;
  height: 200px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;

  &:hover {
    border-color: #1890ff;
    background-color: #f0f7ff;
  }

  label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const UploadIcon = styled.div`
  margin-bottom: 8px;
`;

const UploadText = styled.span`
  color: #8c8c8c;
  font-size: 14px;
`;

const FilePreviewBox = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fafafa;
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background-color: rgba(255, 77, 79, 0.9);
  border-color: #ff4d4f;
  color: white;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #ff4d4f;
    border-color: #ff4d4f;
    color: white;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PdfPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const FilePreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const FileName = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #595959;
  text-align: center;
  word-break: break-word;
  max-width: 100%;
  padding: 0 8px;
`;

